import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchVouchers,
  rejectVoucher as rejectVoucherRequest,
  fetchVouchersDetailsFromApi
} from "../../services/voucherExplorerService";
import FilterBar from "./components/FilterBar";
import StatCards from "./components/StatCards";
import VoucherOverview from "./components/VoucherOverview";
import VoucherTable from "./components/VoucherTable";
import { buildVoucherStats } from "./data";
import moment from "moment-timezone";
import CustomNoData from "../../components/CustomNoData";
import WinLoseVoucherDialog from "../winLoseReport/components/WinLoseVoucherDialog";

const initialTableFilters = {
  voucherId: "",
  user: "",
  type: "",
  status: "",
};
const initialDateFilters = {
  from: moment().format("YYYY-MM-DD"),
  to: moment().format("YYYY-MM-DD"),
};

const buildVoucherDialogData = (voucher, response) => {

  return {
    id: voucher.id,
    voucherNo: voucher.posting,
    voucherStatus: voucher.details.outcome,
    betAmount: voucher.stake,
    loseAmount: voucher.details.grossReturn === 0 ? -voucher.stake : 0,
    winAmount: voucher.details.grossReturn || 0,
    viewDetails: Array.isArray(response) && response.length > 0 ? response : [],
  };
};

export default function VoucherExplorer() {
  const [tableFilters, setTableFilters] = useState(initialTableFilters);
  const [dateFilters, setDateFilters] = useState(initialDateFilters);
  const [appliedDateFilters, setAppliedDateFilters] =
    useState(initialDateFilters);
  const [voucherRows, setVoucherRows] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const overviewRef = useRef(null);
  const [cachedVouchers, setCachedVouchers] = useState([]);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherData, setVoucherData] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const voucherRequestRef = useRef(0);

  const loadVouchers = useCallback(async ({ from, to }) => {
    console.log("Loading vouchers from", from, "to", to);
    setIsLoading(true);
    try {
      const { items } = await fetchVouchers({ startDate: from, endDate: to });
      setVoucherRows(items);
      setCachedVouchers((prev) => [...prev, ...items]);
      setSelectedVoucher(
        (current) =>
          items.find(({ id }) => id === current?.id) || items[0] || null,
      );
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load vouchers");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVouchers(initialDateFilters);
  }, [loadVouchers]);

  const users = useMemo(
    () => [...new Set(voucherRows.map(({ user }) => user).sort((a,b) => a.localeCompare(b)))],
    [voucherRows],
  );
  const filteredVouchers = useMemo(
    () =>
      voucherRows.filter(
        (voucher) =>
          (!tableFilters.voucherId ||
            String(voucher.id)
              .toLowerCase()
              .includes(tableFilters.voucherId.trim().toLowerCase())) &&
          (!tableFilters.user || voucher.user === tableFilters.user) &&
          (!tableFilters.type || voucher.type === tableFilters.type) &&
          (!tableFilters.status || voucher.status === tableFilters.status),
      ),
    [tableFilters, voucherRows],
  );
  const stats = useMemo(
    () => buildVoucherStats(filteredVouchers),
    [filteredVouchers],
  );
  const updateFilter = (name, value) =>
    setDateFilters((current) => ({ ...current, [name]: value }));
  const updateTableFilter = (name, value) =>
    setTableFilters((current) => ({ ...current, [name]: value }));

  useEffect(() => {
    setSelectedVoucher(
      (current) =>
        filteredVouchers.find(({ id }) => id === current?.id) ||
        filteredVouchers[0] ||
        null,
    );
  }, [filteredVouchers]);

  const applyFilters = async (event) => {
    event.preventDefault();
    if (
      dateFilters.from &&
      dateFilters.to &&
      dateFilters.from > dateFilters.to
    ) {
      toast.error("Start date must be before the end date");
      return;
    }
    const dateChanged =
      dateFilters.from !== appliedDateFilters.from ||
      dateFilters.to !== appliedDateFilters.to;
    setAppliedDateFilters(dateFilters);
    if (dateChanged) {
      await loadVouchers(dateFilters);
    } else {
      setVoucherRows(cachedVouchers);
    }
  };

  const resetFilters = async () => {
    const dateChanged = appliedDateFilters.from || appliedDateFilters.to;
    setTableFilters(initialTableFilters);
    setDateFilters(initialDateFilters);
    setAppliedDateFilters(initialDateFilters);
    const loaded = dateChanged
      ? await loadVouchers(initialDateFilters)
      : setVoucherRows(cachedVouchers);
    if (loaded) toast.success("Filters reset");
  };

  const viewVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    //toast.info(`Viewing ${voucher.id}`);
    if (window.innerWidth < 1280)
      requestAnimationFrame(() =>
        overviewRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      );
  };

  const viewDetails = (voucher) => {
    setVoucherDialogOpen(true);
    loadVoucherDetails(voucher);
    //toast.info(`Viewing ${voucher.id}`);
    if (window.innerWidth < 1280)
      requestAnimationFrame(() =>
        overviewRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      );
  };

  const loadVoucherDetails = async (voucher) => {
    const requestId = ++voucherRequestRef.current;
    setVoucherLoading(true);
    setVoucherError(null);
    setVoucherData(buildVoucherDialogData(voucher, null));
    try {
      const data = await fetchVouchersDetailsFromApi(voucher.gid);
      if (requestId === voucherRequestRef.current) {
        setVoucherData(buildVoucherDialogData(voucher, data));
      }
    } catch (error) {
      if (requestId === voucherRequestRef.current) {
        setVoucherError(error?.response?.data?.message || "Failed to load voucher details");
      }
    } finally {
      if (requestId === voucherRequestRef.current) {
        setVoucherLoading(false);
      }
    }
  };

  const closeVoucherDialog = () => {
    voucherRequestRef.current += 1;
    setVoucherDialogOpen(false);
    setVoucherLoading(false);
    setVoucherError(null);
  };

  const rejectVoucher = async (voucher) => {
    if (!window.confirm(`Reject voucher ${voucher.id}?`)) return;
    setRejectingId(voucher.id);
    try {
      await rejectVoucherRequest(voucher.id);
      toast.success(`${voucher.id} was rejected`);
      await loadVouchers(appliedDateFilters);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Unable to reject ${voucher.id}`,
      );
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <main className="min-h-full px-2 pb-8 font-inter text-customInk dark:text-customHeading lg:px-4">
      <WinLoseVoucherDialog
        open={voucherDialogOpen}
        voucher={voucherData}
        loading={voucherLoading}
        error={voucherError}
        onClose={closeVoucherDialog}
        onRetry={() =>
          selectedVoucher && loadVoucherDetails(selectedVoucher)
        }
      />
      <div className="mx-auto max-w-[1680px]">
        <header className="mb-3">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-950 dark:text-white">
            Voucher Explorer
          </h1>
        </header>
        <div className="space-y-3">
          <FilterBar
            dateFilters={dateFilters}
            users={users}
            onDateChange={updateFilter}
            onApply={applyFilters}
            onReset={resetFilters}
            disabled={isLoading}
            filters={tableFilters}
            onChange={updateTableFilter}
          />
          <StatCards items={stats} />
          <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1.45fr)_minmax(430px,1fr)]">
            <VoucherTable
              vouchers={filteredVouchers}
              selectedId={selectedVoucher?.id}
              onSelect={setSelectedVoucher}
              onView={viewVoucher}
              onReject={rejectVoucher}
              rejectingId={rejectingId}
              isLoading={isLoading}
            />
            <div ref={overviewRef}>
              {selectedVoucher ? (
                <VoucherOverview voucher={selectedVoucher} viewDetails={viewDetails} />
              ) : (
                <CustomNoData
                  title="No voucher selected"
                  description="Select a voucher from the table to view its overview."
                  py={24}
                  className="min-h-[260px] shadow-sm"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
